import type { HelpEntry } from './types';

export interface SearchHit {
  entry: HelpEntry;
  score: number;
  /** A short piece of the text around the first match, or the summary. */
  snippet: string;
}

const fold = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

/**
 * Plain matching over title, summary and text: every word of the query has to
 * appear somewhere; a word in the title counts most. Good enough for a few dozen
 * topics, and nothing leaves the browser.
 */
export function searchHelp(entries: HelpEntry[], query: string, textOf: (e: HelpEntry) => string, limit = 20): SearchHit[] {
  const words = fold(query).split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return [];
  const hits: SearchHit[] = [];
  for (const entry of entries) {
    const title = fold(entry.title);
    const summary = fold(entry.summary || '');
    const body = textOf(entry);
    const bodyFolded = fold(body);
    let score = 0;
    let all = true;
    for (const w of words) {
      const inTitle = title.includes(w);
      const inSummary = summary.includes(w);
      const inBody = bodyFolded.includes(w);
      if (!inTitle && !inSummary && !inBody) { all = false; break; }
      score += (inTitle ? 6 : 0) + (title.startsWith(w) ? 2 : 0) + (inSummary ? 3 : 0) + (inBody ? 1 : 0);
    }
    if (!all) continue;
    hits.push({ entry, score, snippet: snippetFor(body, bodyFolded, words) || entry.summary || '' });
  }
  return hits.sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title)).slice(0, limit);
}

function snippetFor(body: string, folded: string, words: string[]): string {
  const at = words.map((w) => folded.indexOf(w)).filter((i) => i >= 0).sort((a, b) => a - b)[0];
  if (at === undefined) return '';
  const start = Math.max(0, body.lastIndexOf(' ', Math.max(0, at - 50)) + 1);
  const end = Math.min(body.length, at + 110);
  return `${start > 0 ? '…' : ''}${body.slice(start, end).trim()}${end < body.length ? '…' : ''}`;
}
